/**
 * Riduzione rumore avanzata (RNNoise in WebAssembly dentro un AudioWorklet), al posto di Krisp
 * (a pagamento e non usabile con LiveKit self-hosted).
 *
 * Libreria: @sapphi-red/web-noise-suppressor (MIT), che usa @shiguredo/rnnoise-wasm (Apache-2.0,
 * RNNoise di xiph, BSD-3). Worklet e WASM sono asset di Vite (`?url`): stesso dominio, niente CDN.
 *
 * Catena del microfono:
 *   getUserMedia (AEC del browser) -> MediaStreamSource -> RnnoiseWorkletNode
 *     -> MediaStreamAudioDestinationNode -> traccia pubblicata su LiveKit (cloni)
 *
 * Perché non `LocalAudioTrack.setProcessor()` di livekit-client: con il make-before-break ogni
 * Room ha la sua LocalAudioTrack (cloni della stessa sorgente); un processore per traccia vorrebbe
 * dire due istanze RNNoise durante un cambio stanza, inizializzazioni asincrone in mezzo alla
 * pubblicazione e l'AudioContext della Room, che può non essere a 48 kHz. Una sola catena
 * condivisa, di cui si pubblicano cloni con `replaceTrack`, è più semplice e si accende/spegne
 * senza ricollegarsi.
 *
 * Sample rate: RNNoise lavora solo a 48 kHz (frame da 480 campioni = 10 ms). Se l'AudioContext
 * principale non è a 48 kHz se ne crea uno dedicato a 48 kHz per il microfono; il browser
 * ricampiona l'ingresso. Se il browser non lo consente (es. Firefox con frequenze diverse) si
 * ricade sul microfono senza RNNoise.
 */

export type NoiseSuppressionStatus = 'off' | 'loading' | 'on' | 'unsupported' | 'error';

export const RNNOISE_SAMPLE_RATE = 48_000;

/** Parte dipendente dal browser, sostituibile nei test. */
export interface NoiseSuppressorBackend {
  /** AudioWorklet e WebAssembly disponibili. */
  isSupported(): boolean;
  /** Contesto a 48 kHz per l'elaborazione: quello principale se va bene, altrimenti uno nuovo. */
  createContext(main: AudioContext): AudioContext;
  /** Carica worklet e WASM nel contesto e crea il nodo RNNoise (mono). */
  createNode(ctx: AudioContext): Promise<AudioNode & { destroy?: () => void }>;
}

export const rnnoiseBackend: NoiseSuppressorBackend = {
  isSupported() {
    return (
      typeof AudioWorkletNode !== 'undefined' &&
      typeof WebAssembly === 'object' &&
      typeof AudioContext !== 'undefined' &&
      'audioWorklet' in AudioContext.prototype
    );
  },
  createContext(main) {
    if (main.sampleRate === RNNOISE_SAMPLE_RATE) return main;
    return new AudioContext({ sampleRate: RNNOISE_SAMPLE_RATE, latencyHint: 'interactive' });
  },
  async createNode(ctx) {
    // Import dinamici: la libreria estende AudioWorkletNode al caricamento (non esiste nei test né
    // nei browser senza AudioWorklet) e così il suo codice resta in un chunk separato.
    const [lib, worklet, wasm, wasmSimd] = await Promise.all([
      import('@sapphi-red/web-noise-suppressor'),
      import('@sapphi-red/web-noise-suppressor/rnnoiseWorklet.js?url'),
      import('@sapphi-red/web-noise-suppressor/rnnoise.wasm?url'),
      import('@sapphi-red/web-noise-suppressor/rnnoise_simd.wasm?url'),
    ]);
    wasmBinary ??= lib.loadRnnoise({ url: wasm.default, simdUrl: wasmSimd.default });
    const binary = await wasmBinary.catch((e: unknown) => {
      wasmBinary = null; // si potrà riprovare
      throw e;
    });
    if (!workletLoaded.has(ctx)) {
      await ctx.audioWorklet.addModule(worklet.default);
      workletLoaded.add(ctx);
    }
    return new lib.RnnoiseWorkletNode(ctx, { maxChannels: 1, wasmBinary: binary });
  },
};

/** Il WASM si scarica una volta per pagina (scelta SIMD/non SIMD fatta da loadRnnoise). */
let wasmBinary: Promise<ArrayBuffer> | null = null;
const workletLoaded = new WeakSet<BaseAudioContext>();

/**
 * Una catena RNNoise per il microfono. Il nodo RNNoise è creato una volta e riusato: il
 * processore della libreria non riceve in modo affidabile il messaggio "destroy", quindi ricrearlo
 * a ogni cambio lascerebbe istanze WASM attive.
 */
export class MicNoiseSuppressor {
  private procCtx: AudioContext | null = null;
  private node: (AudioNode & { destroy?: () => void }) | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private dest: MediaStreamAudioDestinationNode | null = null;

  constructor(
    private readonly backend: NoiseSuppressorBackend,
    private readonly main: AudioContext,
  ) {}

  /** Elabora il microfono grezzo e restituisce lo stream filtrato. Lancia un errore se non si può. */
  async start(raw: MediaStream): Promise<MediaStream> {
    this.stop();
    if (!this.procCtx) this.procCtx = this.backend.createContext(this.main);
    const ctx = this.procCtx;
    if (ctx.state === 'suspended') await ctx.resume().catch(() => undefined);
    if (!this.node) this.node = await this.backend.createNode(ctx);
    if (!this.dest) {
      this.dest = ctx.createMediaStreamDestination();
      this.dest.channelCount = 1;
      this.node.connect(this.dest);
    }
    // Può lanciare NotSupportedError (es. Firefox se il microfono non è a 48 kHz).
    this.source = ctx.createMediaStreamSource(raw);
    this.source.connect(this.node);
    return this.dest.stream;
  }

  /** Scollega il microfono (il nodo resta pronto per un nuovo start). */
  stop(): void {
    try {
      this.source?.disconnect();
    } catch {
      /* già scollegato */
    }
    this.source = null;
  }

  dispose(): void {
    this.stop();
    try {
      this.node?.destroy?.();
      this.node?.disconnect();
    } catch {
      /* ignore */
    }
    this.node = null;
    this.dest = null;
    if (this.procCtx && this.procCtx !== this.main) void this.procCtx.close().catch(() => undefined);
    this.procCtx = null;
  }
}
